import type { StatusCodes } from 'http-status-codes';
import { z } from 'zod';
import type { EApiErrorCode } from './_constants/errorLiterals';
import type { IDataSource } from './_data-sources/base/DataSource.interface';

export enum EQueryAnalysis {
	FRESH_QUERY = 'FRESH_QUERY',
	CONVERSATIONAL = 'CONVERSATIONAL',
	IRRELEVANT = 'IRRELEVANT'
}

export enum EQueryPattern {
	SEARCH_BY_NAME = 'SEARCH_BY_NAME',
	LOOKUP_BY_ID = 'LOOKUP_BY_ID',
	BROWSE_LIST = 'BROWSE_LIST',
	KNOWLEDGE = 'KNOWLEDGE',
	GENERAL = 'GENERAL'
}

export enum EMessageRole {
	HUMAN = 'human',
	ASSISTANT = 'assistant',
	SYSTEM = 'system',
	TOOL = 'tool'
}

export enum EFeedback {
	POSITIVE = 'positive',
	NEGATIVE = 'negative'
}

export enum EUserRole {
	USER = 'user',
	TESTER = 'tester',
	ADMIN = 'admin'
}

export interface IMessageResponse {
	message: string;
}

export interface IErrorResponse extends IMessageResponse {
	code: EApiErrorCode;
}

export interface IApiResponse<T> {
	status: StatusCodes;
	data: T | null;
	error: IErrorResponse | null;
}

export type ApiSuccessResult<T> = {
	data: T;
	status?: StatusCodes;
	headers?: Record<string, string>;
};

export type ApiHandlerResult<T> = ApiSuccessResult<T> | Response;

export interface IConversation {
	id: string;
	userId: string;
	createdAt: Date;
	updatedAt: Date;
	title: string;
	isPublic: boolean;
	totalCitations?: number;
}

export interface IPublicConversationCard {
	id: string;
	title: string;
	totalCitations?: number;
}

export interface IPaginatedConversations {
	conversations: IConversation[];
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
}

export interface IMessage {
	id: string;
	conversationId: string;
	text: string;
	createdAt: Date;
	updatedAt: Date;
	role: EMessageRole;
	agentActivities: AgentActivity[];
	/** Display citations — populated during streaming, lazy-loaded for historical messages */
	citations?: Citation[];
	/** Token usage for this message (only for assistant messages) */
	tokenUsage?: TokenUsage | null;
	/** User feedback (thumbs up/down) for assistant messages */
	feedback?: EFeedback | null;
	/** HITL interrupt question awaiting user input (only for interrupted assistant messages) */
	interruptQuestion?: InterruptQuestion | null;
	/** Follow-up suggestion questions (only for the latest assistant message) */
	suggestions?: string[];
}

/**
 * Lightweight message for fast initial load (excludes heavy JSONB fields).
 */
export type IMessageLightweight = Pick<
	IMessage,
	'id' | 'conversationId' | 'text' | 'role' | 'feedback' | 'createdAt' | 'interruptQuestion'
>;

/**
 * Heavy message details loaded on demand (for tab expansion).
 * Data pool entries and citations come from relational tables, not message JSONB.
 */
export interface IMessageDetails {
	agentActivities: AgentActivity[];
	dataPoolEntries: IDataPoolEntry[];
	dbCitations: ICitation[];
	tokenUsage?: TokenUsage | null;
}

/** Token usage metrics for AI model calls */
export interface TokenUsage {
	/** Number of tokens in the input/prompt */
	inputTokens: number;
	/** Number of tokens in the output/completion */
	outputTokens: number;
	/** Number of cache tokens (read + creation) */
	cacheTokens: number;
	/** Total tokens (input + output + cache) */
	totalTokens: number;
	/** AI model that generated these tokens */
	model?: string;
	/** Breakdown by step/operation */
	breakdown?: TokenUsageStep[];
}

/** Token usage for a specific step/operation */
export interface TokenUsageStep {
	/** Step identifier (e.g., 'analyze_query', 'plan_steps', 'schema_selection') */
	step: string;
	/** Human-readable label */
	label: string;
	/** Input tokens for this step */
	inputTokens: number;
	/** Output tokens for this step */
	outputTokens: number;
	/** Cache tokens for this step (read + creation) */
	cacheTokens: number;
	/** Total tokens for this step */
	totalTokens: number;
}

export interface SchemaChunk {
	id: string;
	content: string;
	metadata: {
		category:
			| 'type'
			| 'query'
			| 'relationship'
			| 'filter'
			| 'concept'
			| 'example'
			| 'endpoint'
			| 'sql-analytics'
			| 'sql-reference';
		/** REST API endpoint path template, e.g. '/api/v2/{proposalType}' (only for endpoint category) */
		endpoint?: string;
		/** HTTP method for this endpoint (only for endpoint category) */
		method?: 'GET' | 'POST';
		graphqlType?: string;
		relatedTypes?: string[];
		examples: string[];
		keywords?: string[];
		dataSource: EDataSource;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		paramsSchema?: z.ZodObject<any>;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		responseSchemaHint?: z.ZodType<any>; // only a hint, response schema might change, do not rely on this absolutely
		/** Dot-path to the primary data array in the response, e.g. "topic_list.topics".
		 *  Guides distillation when responses have multiple arrays at different depths. */
		primaryArrayPath?: string;
	};
}

export interface SchemaSearchResultItem extends SchemaChunk {
	relevanceScore: number;
}

// ==================== Human-in-the-Loop Types ====================

/** HITL question sent to frontend when the AI needs user input to continue */
export interface InterruptQuestion {
	id: string;
	type: 'disambiguation';
	question: string;
	options: Array<{ label: string; value: string; description?: string }>;
	allowOther: boolean;
}

/** User's answer to an interrupt question */
export interface InterruptAnswer {
	questionId: string;
	selectedValue: string;
	isOther: boolean;
}

// Processing steps for status events
export type ProcessingStep =
	| 'analyzing_query'
	| 'planning_steps'
	| 'searching_schema'
	| 'generating_query'
	| 'fetching_data'
	| 'evaluating_step'
	| 'replanning'
	| 'resolving_from_pool'
	| 'streaming_response'
	| 'awaiting_input';

// Agent activity categories
export type AgentActivityCategory =
	| 'classification'
	| 'schema_search'
	| 'query_generated'
	| 'query_result'
	| 'error'
	| 'data_source_routing'
	| 'pool_reuse'
	| 'stopped'
	| 'thinking'
	| 'validation';

// ==================== Stream Event Types ====================

/** Complete message (human or assistant) */
export interface StreamMessageEvent {
	type: 'message';
	message: IMessage;
}

/** Signals the start of AI response streaming */
export interface StreamStartEvent {
	type: 'stream_start';
	conversationId: string;
}

/** Streaming AI text chunk */
export interface StreamContentChunkEvent {
	type: 'content_chunk';
	chunk: string;
}

/** Processing status update */
export interface StreamStatusEvent {
	type: 'status';
	message: string;
	step: ProcessingStep;
}

/** Agent activity - used during streaming and stored in messages */
export interface AgentActivity {
	type: 'agent_activity';
	category: AgentActivityCategory;
	title: string;
	content: string;
	/** Date on server, ISO string after JSON serialization (SSE/DB) */
	timestamp: Date | string;
	/** Step identifier for grouping parallel activities (e.g., "step_1") */
	stepId?: string;
	/** Optional metadata — currently only used by debug_activity events, kept for DB compat */
	metadata?: Record<string, unknown>;
}

/** Citations update during streaming */
export interface StreamEvidenceEvent {
	type: 'evidence';
	citations: Citation[];
}

/** Stream complete with final message */
export interface StreamEndEvent {
	type: 'stream_end';
	message: IMessage;
	/** True if the graph was interrupted and is awaiting user input */
	interrupted?: boolean;
	/** The message ID (thread_id) needed to resume the interrupted graph */
	interruptMessageId?: string;
}

/** HITL interrupt — AI needs user input to continue */
export interface StreamInterruptRequestEvent {
	type: 'interrupt_request';
	question: InterruptQuestion;
}

/** Follow-up suggestions after a response */
export interface StreamSuggestionsEvent {
	type: 'suggestions';
	suggestions: string[];
}

/** Error occurred */
export interface StreamErrorEvent {
	type: 'error';
	error: string;
	code?: string;
	/** When the limit will reset (for token limit errors) */
	resetAt?: string;
}

/** Token usage update during streaming */
export interface StreamTokenUsageEvent {
	type: 'token_usage';
	tokenUsage: TokenUsage;
}

/** Replace full streaming content (used to swap citation markers after streaming) */
export interface StreamContentReplaceEvent {
	type: 'content_replace';
	text: string;
}

/** Model thinking/reasoning chunk (displayed in thinking tab) */
export interface StreamThinkingChunkEvent {
	type: 'thinking_chunk';
	chunk: string;
}

/** Debug activity - only logged to debug files, never sent to frontend */
export interface DebugActivity {
	type: 'debug_activity';
	category: 'prompt' | 'truncation' | 'raw_data' | 'schema' | 'plan' | 'wave' | 'validation' | 'distillation';
	title: string;
	content: string;
	metadata?: Record<string, unknown>;
	timestamp: Date;
}

/** Conversation successfully created (optimistic flow) */
export interface StreamConversationCreatedEvent {
	type: 'conversation_created';
	conversation: IConversation;
}

export type StreamEvent =
	| StreamConversationCreatedEvent
	| StreamMessageEvent
	| StreamStartEvent
	| StreamContentChunkEvent
	| StreamContentReplaceEvent
	| StreamThinkingChunkEvent
	| StreamStatusEvent
	| AgentActivity
	| StreamEvidenceEvent
	| StreamTokenUsageEvent
	| StreamEndEvent
	| StreamErrorEvent
	| StreamInterruptRequestEvent
	| StreamSuggestionsEvent
	| DebugActivity;

// Data Source Types
//
// Declaration order is load-bearing: it determines the order in which sources are rendered
// into the planner/replanner system prompts (see helpers/sourceMetadataPrompt.server.ts).
// Reordering invalidates the Gemini implicit prompt cache prefix until the next deployment.
export enum EDataSource {
	INDEXER = 'indexer',
	POLKASSEMBLY = 'polkassembly',
	SUBSQUARE = 'subsquare',
	SUBSCAN = 'subscan',
	COINGECKO = 'coingecko',
	CRYPTOCOMPARE = 'cryptocompare',
	POLKADOT_WIKI = 'polkadot-wiki',
	POLKADOT_FORUM = 'polkadot-forum'
}

export interface IDataSourceMetadata {
	name: EDataSource;
	description: string;
	version: string;
	apiType: 'rest' | 'sql';
	/** What this data source can do (for step planner context) */
	capabilityDescription?: string;
	/** How long fetched data stays fresh, in milliseconds. Used for data pool reuse decisions. */
	ttlMs: number;
	/** Per-source planning instructions for the LLM step planner (e.g., endpoint/network requirements) */
	planningRules?: string;
	/** Hard structural limits — things this source cannot do at all. */
	limitations?: string[];
	/** Coverage caveats — data may be partial, stale, sampled, or biased. */
	coverageNotes?: string[];
	/** Query shapes where this source is the right pick. */
	preferredFor?: string[];
	/** Query shapes where another source is better — discourages mis-routing. */
	avoidFor?: string[];
	/**
	 * Per-endpoint gotchas keyed by endpoint path. Rendered only when that endpoint
	 * appears in RELEVANT ENDPOINTS for the query.
	 */
	endpointCaveats?: Record<string, string>;
}

/** Pre-rendered prompt sections built once per process from enabled data sources. */
export interface SourceMetadataPromptSections {
	/** ## AVAILABLE DATA SOURCES & CAPABILITIES */
	capabilities: string;
	/** ## LIMITATIONS — empty string when no source declares any */
	limitations: string;
	/** ## DATA COVERAGE NOTES — empty string when no source declares any */
	coverageNotes: string;
	/** ## SOURCE SELECTION GUIDANCE — empty string when no source declares any */
	selectionGuidance: string;
	/** Per-source endpoint/method/network mechanics — empty string when no source declares any */
	planningRules: string;
}

export enum ENetwork {
	POLKADOT = 'polkadot',
	KUSAMA = 'kusama',
	POLKADOT_ASSET_HUB = 'polkadotAssetHub',
	KUSAMA_ASSET_HUB = 'kusamaAssetHub'
}

// ==================== Graph Context Types ====================

export interface GraphConstants {
	MESSAGE_HISTORY_WINDOW: number;
	HYBRID_SEARCH_MAX_RESULTS: number;
	PRE_RETRIEVAL_MAX_RESULTS: number;
	PRE_RETRIEVAL_VECTOR_PER_SOURCE: number;
	STEP_VECTOR_PER_SOURCE: number;
	SCHEMA_FALLBACK_TOP_N: number;
	RRF_K: number;
	POOL_DIGEST_CAP: number;
	MAX_REPLANS: number;
	FETCH_TIMEOUT_MS: number;
}

export interface GraphContext {
	dataSources: Partial<Record<EDataSource, IDataSource>>;
	schemaChunksBySource: Partial<Record<EDataSource, SchemaChunk[]>>;
	constants: GraphConstants;
}

// ==================== AI Internal Types ====================

/** Internal step result used during workflow execution for dependency resolution */
export interface InternalStepResult {
	stepId: string;
	dataSource: EDataSource;
	endpoint?: string;
	rawData: unknown;
	fetchedAt: string;
	extractedValue?: string;
	failed?: boolean;
	failureReason?: string;
	// Query-relevant filtered data + analysis — used by format_response instead of rawData when present.
	// Produced by per-step distillation for large datasets; undefined for small datasets (pass-through).
	distilledData?: string;
}

/** Compact summary of a data pool entry for the classifier (no raw data, just shape) */
export interface PoolDigestEntry {
	id: string;
	dataSource: EDataSource;
	endpoint: string | null;
	stepDescription: string | null;
	extractedValue: string | null;
	availableFields: string;
	fetchedAt: string;
}

/** Citation references a specific path within the raw data */
export interface Citation {
	/** Citation number shown as [n] in response */
	index: number;
	/** JSON path to the specific data, e.g. "account.balance" or "proposals[0].title" */
	jsonPath: string;
}

// ==================== Data Pool Types ====================

/** Accumulated during executeStep, batch-written to data_pool table after message save */
export interface PendingDataPoolEntry {
	stepId: string;
	stepDescription?: string;
	stepIndex: number;
	dataSource: EDataSource;
	endpoint?: string;
	rawData: unknown;
	extractedValue?: string;
	failed: boolean;
	failureReason?: string;
	fetchedAt: string;
}

/** Built during formatResponse, batch-written to citations table after message save */
export interface PendingCitationMapping {
	stepId: string;
	citationIndex: number;
	jsonPath: string;
}

/** A single data fetch stored in the data_pool table */
export interface IDataPoolEntry {
	id: string;
	conversationId: string;
	messageId: string;
	stepId: string;
	stepDescription?: string | null;
	stepIndex: number;
	dataSource: EDataSource;
	endpoint?: string | null;
	rawData: unknown;
	extractedValue?: string | null;
	failed: boolean;
	failureReason?: string | null;
	fetchedAt: string;
	createdAt: string;
}

/** A citation stored in the citations table, linking a display [n] to a data pool entry */
export interface ICitation {
	id: string;
	messageId: string;
	dataPoolEntryId: string;
	citationIndex: number;
	jsonPath: string;
}

// ==================== Client Types ====================

export type PapiStatus = 'idle' | 'initializing' | 'ready' | 'error';

/** Represents an on-chain identity with relevant fields */
export interface OnChainIdentity {
	/** Display name from identity registration */
	displayName: string | null;
	/** Whether identity has received a positive judgement */
	isVerified: boolean;
	/** Judgement level: Reasonable, KnownGood, or null */
	judgement: 'Reasonable' | 'KnownGood' | null;
	/** Legal name if provided */
	legal?: string;
	/** Website URL if provided */
	web?: string;
	/** Twitter handle if provided */
	twitter?: string;
	/** Email if provided */
	email?: string;
	/** Which chain the identity was found on */
	source: 'polkadot' | 'kusama';
}

// ==================== Re-exports (types derived from infrastructure, co-located with definitions) ====================

// Schema-derived types — z.infer from Zod schemas in ai.schema.ts
export type {
	QueryAnalysis,
	QueryStepOutput,
	StepPlan,
	RESTAPIQuery,
	SQLQuery,
	RESTAPIParamsOnly
} from './_services/ai/ai.schema';

// LangGraph state types — derived from Annotation.Root / RunnableConfig in state.ts
export type { WorkflowState, WorkerReturn, WorkflowConfig } from './_services/ai/graph/state';
